import { useEffect, useState } from 'react';
import { formatRelativeDate, type ActivityEntry, type JournalEntry, type JournalStats, type ListActivityResponse, type PorteeJournal } from '@mlc/shared';
import { listActivity, listJournal, markActivitySeen, shareCookLogs } from './api';
import { Link, navigate, paths, useSearchParam, useUrl, withSearchParam, withSearchParams } from './router';
import { Banner, Button, Card, EmptyState, FOCUS_RING, Icon, Loading, PageHeader, Select, TextInput, buttonClasses } from './ui';
import { useSession } from './session';

/**
 * Journal transversal (US-30) : « je veux relire ce que j'ai cuisiné, et ce
 * que j'en ai pensé ».
 *
 * C'est l'écran qui rend visible le positionnement du produit. Ni Paprika, ni
 * Crouton, ni Mela, ni NYT Cooking ne gardent trace de *vos* tentatives —
 * cette application le fait depuis US-10, mais l'information restait enfermée
 * dans la fiche de chaque recette, en dernier bloc, sous les notes.
 *
 * L'écran est donc une **chronologie**, pas une liste de recettes : ce qu'on
 * relit, c'est une succession de soirs de cuisine.
 */

function Stat({ label, value }: { label: string; value: string }): React.JSX.Element {
  return (
    <Card className="flex flex-col gap-1">
      <span className="text-label">{label}</span>
      <span className="text-data text-lg font-semibold text-foreground">{value}</span>
    </Card>
  );
}

function StatsHeader({ stats }: { stats: JournalStats }): React.JSX.Element | null {
  if (stats.total === 0) return null;
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <Stat label="Ce mois-ci" value={`${stats.cooked_this_month} ${stats.cooked_this_month > 1 ? 'plats' : 'plat'}`} />
      <Stat label="En tout" value={`${stats.total} ${stats.total > 1 ? 'réalisations' : 'réalisation'}`} />
      {stats.most_cooked !== null && (
        <Stat label="La plus refaite" value={`${stats.most_cooked.title} · ${stats.most_cooked.times}×`} />
      )}
      {stats.best_rated !== null && (
        <Stat label="Meilleure note" value={`${stats.best_rated.title} · ${stats.best_rated.rating}/5`} />
      )}
    </div>
  );
}

function EntryCard({ entry, now, moi }: {
  entry: JournalEntry;
  now: number;
  /** Identifiant du membre connecté — **jamais son nom** (US-52) : deux Claire ne doivent pas se confondre. */
  moi: string | null;
}): React.JSX.Element {
  return (
    <li>
      <Card className="flex gap-4">
        {entry.photo_path !== null ? (
          <img
            src={`/api/photos/${entry.photo_path}`}
            // `alt` renseigné (US-65) : une photo de réalisation est le contenu
            // de l'entrée, pas sa décoration.
            alt={`${entry.recipe_title}${entry.member_id === moi || entry.member_name == null ? '' : `, par ${entry.member_name}`}`}
            loading="lazy"
            className="size-24 shrink-0 rounded-control object-cover"
          />
        ) : entry.recipe_image_path !== null ? (
          <img
            src={`/api/photos/${entry.recipe_image_path}`}
            alt=""
            loading="lazy"
            className="size-24 shrink-0 rounded-control object-cover opacity-70"
          />
        ) : null}

        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <p className="flex flex-wrap items-baseline gap-2">
            {/* `cook_log.recipe_id` est NOT NULL et la recette n'est jamais
                supprimée (§3 : « on archive, on ne supprime jamais ») : le
                titre est toujours là, y compris pour une recette archivée. */}
            <Link to={paths.recipe(entry.recipe_id)} className="font-display font-semibold text-foreground">
              {entry.recipe_title}
            </Link>
            <span className="text-data text-sm text-foreground-muted">
              {formatRelativeDate(entry.cooked_at, now)}
            </span>
            {/* L'auteur, quand ce n'est pas soi (US-65) : sur la vue famille,
                « trop liquide » sans savoir de qui ne veut rien dire. */}
            {entry.member_id !== moi && entry.member_name != null && (
              <span className="text-sm text-foreground-secondary">par {entry.member_name}</span>
            )}
            {entry.rating !== null && (
              <span className="text-data text-sm font-medium text-primary">
                <Icon name="etoile" size={0.9} className="mr-0.5" />
                <span className="sr-only">Noté </span>
                {entry.rating}/5
              </span>
            )}
          </p>

          {entry.what_happened !== null && <p className="text-sm text-foreground">{entry.what_happened}</p>}
          {entry.next_time !== null && (
            <p className="text-sm text-foreground-secondary">
              <span className="font-medium">La prochaine fois :</span> {entry.next_time}
            </p>
          )}
          {entry.actual_minutes !== null && (
            <p className="text-meta">
              <span className="text-data">{entry.actual_minutes} min</span> en vrai
            </p>
          )}
        </div>
      </Card>
    </li>
  );
}

/**
 * Ce qui est nouveau depuis la dernière visite (US-68).
 *
 * Quatre natures d'événement, une seule forme : réalisations partagées,
 * commentaires, réactions et propositions, dans le périmètre visible du
 * demandeur — un décompte bâti sur toute la base trahirait l'activité sur des
 * recettes invisibles.
 *
 * Ses propres actions n'y sont pas : on sait ce qu'on a fait, et un repère qui
 * ne descend jamais à zéro est un repère qu'on cesse de lire.
 */
function QuoiDeNeuf({ now }: { now: number }): React.JSX.Element | null {
  const [flux, setFlux] = useState<ListActivityResponse | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    Promise.resolve()
      .then(() => listActivity(controller.signal))
      .then((recu) => { setFlux(Array.isArray(recu.entries) ? recu : null); })
      .catch(() => { /* le journal reste lisible sans son flux */ });
    return () => { controller.abort(); };
  }, []);

  if (flux === null || flux.entries.length === 0) return null;

  // **`seen_at` peut être `null`** (US-80) : sur la tablette de cuisine, il n'y
  // a personne pour avoir « vu jusque-là ». Rien n'est alors marqué comme
  // nouveau, et le bouton « J'ai vu » ne s'affiche pas — la route qui le sert
  // répond 404 à un appareil depuis US-68.
  const repere = flux.seen_at;
  const estNouvelle = (entree: ActivityEntry): boolean => repere !== null && entree.quand > repere;
  const nouvelles = flux.entries.filter(estNouvelle);

  return (
    <section aria-labelledby="titre-nouveautes" className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <h2 id="titre-nouveautes" className="text-heading-2">
          Quoi de neuf
          {nouvelles.length > 0 && <span className="text-data text-base font-normal"> ({nouvelles.length})</span>}
        </h2>
        {nouvelles.length > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="compact"
            onClick={() => {
              markActivitySeen()
                .then((recu) => { setFlux(Array.isArray(recu.entries) ? recu : null); })
                .catch(() => { /* rien à dire : le repère se reposera */ });
            }}
          >
            J’ai vu
          </Button>
        )}
      </div>
      <ul className="flex flex-col gap-1">
        {flux.entries.slice(0, 8).map((entree) => (
          <li key={`${entree.kind}-${entree.id}`} className="text-sm">
            {estNouvelle(entree) && (
              <span aria-hidden="true" className="mr-1 text-primary">•</span>
            )}
            <span className="font-medium">{entree.member_name ?? 'La maison'}</span>
            {' '}
            {LIBELLES_ACTIVITE[entree.kind]}{' '}
            <Link to={paths.recipe(entree.recipe_id)} className="underline">{entree.recipe_title}</Link>
            {' · '}
            <span className="text-data text-foreground-muted">{formatRelativeDate(entree.quand, now)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Ce que chaque nature d'événement raconte, en une phrase courte. */
const LIBELLES_ACTIVITE: Record<ActivityEntry['kind'], string> = {
  realisation: 'a cuisiné',
  commentaire: 'a commenté une réalisation de',
  reaction: 'a réagi à une réalisation de',
  proposition: 'a proposé un changement sur',
};

export function Journal(): React.JSX.Element {
  const [entries, setEntries] = useState<JournalEntry[] | null>(null);
  const [stats, setStats] = useState<JournalStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Instant figé au montage : un rendu doit être pur, et « il y a 3 semaines »
  // n'a pas besoin d'être recalculé à la seconde.
  const [now] = useState(() => Date.now());

  // Les filtres vivent dans l'URL comme partout ailleurs (US-13) : une vue du
  // journal est partageable et restituée par le bouton retour.
  const url = useUrl();
  const query = useSearchParam('q');
  const minRating = useSearchParam('note');
  // Filtre par recette (US-30) : c'est le lien « Voir dans le journal » de la
  // fiche qui l'amène, jamais une saisie ici.
  const recipeId = useSearchParam('recette');
  /**
   * Portée (US-65). Dans l'URL comme les autres filtres : une vue du journal
   * se partage et se restitue par le bouton retour. « Mon journal » est le
   * défaut — le journal est d'abord le sien.
   */
  const portee: PorteeJournal = useSearchParam('portee') === 'famille' ? 'famille' : 'moi';
  const session = useSession();
  const moi = session?.member?.id ?? null;
  const [publication, setPublication] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    listJournal({ query, minRating, recipeId, portee, signal: controller.signal })
      .then((data) => { setEntries(data.entries); setStats(data.stats); setError(null); })
      .catch((e: unknown) => {
        if (e instanceof Error && e.name !== 'AbortError') setError(e.message);
      });
    return () => { controller.abort(); };
  }, [query, minRating, recipeId, portee, publication]);

  /**
   * Publier son passé d'un geste (US-65).
   *
   * Après la migration, aucune réalisation n'est partagée : personne n'a écrit
   * ses ratages en sachant qu'ils seraient lus. Sans ce geste de masse,
   * personne ne remonterait son historique une par une — la leçon exacte du
   * tri en lot d'US-49.
   *
   * Les réalisations sur une recette privée sont ignorées par le serveur : on
   * envoie les siennes, il publie ce qui est publiable.
   */
  function publierMonPasse(aPublier: JournalEntry[]): void {
    setError(null);
    shareCookLogs(aPublier.map((entree) => entree.id), true)
      .then((resultat) => {
        setPublication(
          resultat.updated === 0
            ? 'Aucune réalisation à publier : elles portent sur des recettes gardées pour vous.'
            : `${String(resultat.updated)} réalisation${resultat.updated > 1 ? 's' : ''} visible${resultat.updated > 1 ? 's' : ''} de la famille.`,
        );
      })
      .catch((e: unknown) => { setError(e instanceof Error ? e.message : String(e)); });
  }

  const hasFilters = query !== '' || minRating !== '' || recipeId !== '';

  return (
    <div className="flex flex-col gap-6">
      {/*
        **Plus de « ← Bibliothèque »** (refonte éditoriale). Le journal est une
        destination de la navigation principale, au même rang que la
        bibliothèque : lui mettre un lien de retour vers elle proposait de
        remonter à un parent qui n'existe pas. Le même lien orphelin ouvrait
        la Semaine, les Courses et les Réglages, tous des onglets de premier
        niveau. Il ne reste que là où un écran est réellement l'enfant d'un
        autre — la fiche recette, le mode cuisine.
      */}
      <PageHeader
        title="Journal"
        lede={
          portee === 'famille'
            ? 'Vos réalisations, plus celles que la famille a publiées.'
            : 'Ce que vous avez cuisiné, et ce que vous en avez pensé.'
        }
        actions={
          /* Les deux portées, en sélecteur segmenté (refonte éditoriale) :
             c'était une paire de boutons dont l'actif prenait la variante
             primaire — un aplat orange plein, réservé à l'action que la page
             existe pour faire, employé ici comme marqueur de sélection.
             Ce sont des liens et non des boutons (chaque portée est une
             adresse, US-13), d'où l'habillage repris à la main plutôt que le
             composant `SegmentedControl`, qui rend des `<button>`. */
          <div role="group" aria-label="Portée du journal" className="inline-flex rounded-control bg-surface-sunken p-1">
            {([['moi', 'Mon journal'], ['famille', 'La famille']] as const).map(([valeur, label]) => {
              const actif = portee === valeur;
              return (
                <Link
                  key={valeur}
                  to={withSearchParam(url, 'portee', valeur === 'moi' ? '' : valeur)}
                  aria-current={actif ? 'page' : undefined}
                  className={`inline-flex min-h-touch-min items-center rounded-[0.375rem] px-4 text-sm font-medium no-underline transition duration-(--duration-quick) ease-entrance ${FOCUS_RING} ${
                    actif ? 'bg-surface text-foreground shadow-card' : 'text-foreground-secondary hover:text-foreground'
                  }`}
                >
                  {label}
                </Link>
              );
            })}
          </div>
        }
      />

      {recipeId !== '' && entries !== null && (
        <p className="text-meta">
          Réalisations de « {entries[0]?.recipe_title ?? 'cette recette'} ».{' '}
          <Link to={paths.journal} className="underline">Voir tout le journal</Link>
        </p>
      )}

      {error !== null && <Banner tone="danger">Impossible de charger le journal : {error}</Banner>}

      {publication !== null && <Banner tone="info">{publication}</Banner>}

      {/* « Quoi de neuf » (US-68). Ici et non dans une sixième entrée de
          navigation : ce n'est pas une destination de plus, c'est ce que le
          journal familial a de neuf. Ouvrir le journal ne remet pas le repère
          à zéro tout seul — c'est un geste, sinon la liste disparaîtrait sous
          les yeux de qui vient de l'ouvrir. */}
      <QuoiDeNeuf now={now} />

      {/* Publier son passé (US-65) : proposé seulement quand il y a quelque
          chose à publier, et jamais sur la vue famille — on ne publie que le
          sien. */}
      {portee === 'moi' && entries !== null && entries.some((entree) => entree.shared !== 1) && (
        <Button
          type="button"
          variant="secondary"
          className="w-fit"
          onClick={() => { publierMonPasse(entries.filter((entree) => entree.shared !== 1)); }}
        >
          Rendre mes réalisations visibles de la famille
        </Button>
      )}

      {stats !== null && <StatsHeader stats={stats} />}

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-1 flex-col gap-1 text-sm">
          Chercher dans les remarques
          <TextInput
            type="search"
            value={query}
            placeholder="ex. trop liquide"
            onChange={(e) => { navigate(withSearchParams(url, { q: e.target.value }), { replace: true }); }}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Note minimale
          <Select
            aria-label="Note minimale"
            value={minRating}
            onChange={(e) => { navigate(withSearchParams(url, { note: e.target.value }), { replace: true }); }}
          >
            <option value="">Toutes</option>
            <option value="3">3/5 et plus</option>
            <option value="4">4/5 et plus</option>
            <option value="5">5/5</option>
          </Select>
        </label>
      </div>

      {entries === null && <Loading label="votre journal" />}

      {entries !== null && entries.length === 0 && (
        hasFilters ? (
          <EmptyState
            title="Aucune réalisation ne correspond."
            description="La recherche porte sur vos remarques, pas sur les titres de recettes."
            action={
              <Link to={paths.journal} className={buttonClasses('secondary')}>
                Tout effacer
              </Link>
            }
          />
        ) : (
          <EmptyState
            title="Votre journal est vide."
            description="Après avoir cuisiné, notez ce qui s’est passé : c’est ce qui rendra la prochaine fois meilleure."
            action={<Link to={paths.library} className={buttonClasses('primary')}>Choisir une recette</Link>}
          />
        )
      )}

      {entries !== null && entries.length > 0 && (
        <ul data-testid="journal-entries" className="flex flex-col gap-3">
          {entries.map((entry) => (
            <EntryCard key={entry.id} entry={entry} now={now} moi={moi} />
          ))}
        </ul>
      )}
    </div>
  );
}
